import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { AsyncPipe, CommonModule, JsonPipe, NgTemplateOutlet } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_CARD_CONFIG, MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { map, startWith } from 'rxjs';
import { SplUiBadge, SplUiSpinner } from '../../components';
import { SplUiIcon } from '../../directives';

@Component({
  selector: 'spl-ui-theme-preview-feature',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatToolbarModule,
    MatListModule,
    MatTableModule,
    MatSelectModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatMenuModule,
    MatExpansionModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    SplUiIcon,
    SplUiSpinner,
    SplUiBadge,
    RouterLink,
    NgTemplateOutlet,
  ],
  providers: [
    {
      provide: MAT_CARD_CONFIG,
      useValue: { appearance: 'outlined' },
    },
  ],
  templateUrl: './theme-preview.feature.html',
  styleUrl: './theme-preview.feature.scss',
})
export class SplUiThemePreviewFeature {
  async toClipBoard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }

  // Colors
  colors = [
    { label: 'Primary', value: 'var(--mat-sys-primary)' },
    { label: 'Primary Container', value: 'var(--mat-sys-primary-container)' },
    { label: 'Primary On Container', value: 'var(--mat-sys-on-primary-container)' },
    { label: 'Primary State', value: 'var(--mat-sys-primary)', opacityHex: '14' },
    { label: 'Secondary', value: 'var(--mat-sys-secondary)' },
    { label: 'Secondary Container', value: 'var(--mat-sys-secondary-container)' },
    { label: 'Secondary On Container', value: 'var(--mat-sys-on-secondary-container)' },
    { label: 'Secondary State', value: 'var(--mat-sys-secondary)', opacityHex: '14' },
    { label: 'Tertiary', value: 'var(--mat-sys-tertiary)' },
    { label: 'Tertiary Container', value: 'var(--mat-sys-tertiary-container)' },
    { label: 'Tertiary On Container', value: 'var(--mat-sys-on-tertiary-container)' },
    { label: 'Tertiary State', value: 'var(--mat-sys-tertiary)', opacityHex: '14' },
    { label: 'Error', value: 'var(--mat-sys-error)' },
    { label: 'Error Container', value: 'var(--mat-sys-error-container)' },
    { label: 'Error On Container', value: 'var(--mat-sys-on-error-container)' },
    { label: 'Error State', value: 'var(--mat-sys-error)', opacityHex: '14' },
    { label: 'Outline', value: 'var(--mat-sys-outline)' },
    { label: 'Outline Variant (Border/Divider)', value: 'var(--mat-sys-outline-variant)' },
    { label: 'On Surface (Input Text)', value: 'var(--mat-sys-on-surface-variant)' },
  ].map((color) => ({ ...color, getHex: () => this.getCssVariableHex(color.value, color.opacityHex) }));

  getCssVariableHex(variable: string, opacityHex?: string): string {
    const variableName = variable.replace(/(var\(|\))/g, '');

    // Remove '--' prefix if provided
    const cleanName = variableName.startsWith('--') ? variableName : `--${variableName}`;

    // Get the CSS variable value from :root
    const cssValue = getComputedStyle(document.documentElement).getPropertyValue(cleanName).trim();

    return cssValue.replace(/(light-dark\(|,.*)/g, '').concat(opacityHex ?? '');
  }
  // ---

  // Dialog
  readonly dialog = inject(MatDialog);

  openDialog() {
    const dialogRef = this.dialog.open(DialogContentExampleDialog, {
      data: {
        firstName: 'John',
        secondName: 'Doe',
        email: 'john.doe@mail.com',
        birthDate: '15.06.2005',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${JSON.stringify(result)}`);
    });
  }
  // ---

  // Autocomplete
  myControl = new FormControl('');
  options: string[] = ['One', 'Two', 'Three'];
  filteredOptions = this.myControl.valueChanges.pipe(
    startWith(''),
    map((value) => this._filter(value || '')),
  );

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.options.filter((option) => option.toLowerCase().includes(filterValue));
  }
  // ---

  // Snackbar
  private _snackBar = inject(MatSnackBar);

  openSnackBar() {
    this._snackBar.open('Cannonball!!', 'Splash', {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: 2000,
    });
  }
  // ---

  // Checkbox
  readonly task = signal<Task>({
    name: 'Parent task',
    completed: false,
    subtasks: [
      { name: 'Child task 1', completed: true },
      { name: 'Child task 2', completed: false },
      { name: 'Child task 3', completed: false },
    ],
  });

  readonly partiallyComplete = computed(() => {
    const task = this.task();
    if (!task.subtasks) {
      return false;
    }
    return task.subtasks.some((t) => t.completed) && !task.subtasks.every((t) => t.completed);
  });

  update(completed: boolean, index?: number) {
    this.task.update((task) => {
      if (index === undefined) {
        task.completed = completed;
        task.subtasks?.forEach((t) => (t.completed = completed));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        task.subtasks![index].completed = completed;
        task.completed = task.subtasks?.every((t) => t.completed) ?? true;
      }
      return { ...task };
    });
  }
  // ---

  // Expansion
  step = signal(0);

  setStep(index: number) {
    this.step.set(index);
  }

  nextStep() {
    this.step.update((i) => i + 1);
  }

  prevStep() {
    this.step.update((i) => i - 1);
  }
  // ---
}

@Component({
  selector: 'spl-ui-dialog-content-example-dialog',
  template: `
    <h2 mat-dialog-title>Install Angular</h2>
    <mat-dialog-content class="mat-typography">
      <h3>Dialog injected data</h3>
      <code>
        {{ data | json }}
      </code>

      <h3>Develop across all platforms</h3>
      <p>
        Learn one way to build applications with Angular and reuse your code and abilities to build apps for any
        deployment target. For web, mobile web, native mobile and native desktop.
      </p>

      <h3>Speed &amp; Performance</h3>
      <p>
        Achieve the maximum speed possible on the Web Platform today, and take it further, via Web Workers and
        server-side rendering. Angular puts you in control over scalability. Meet huge data requirements by building
        data models on RxJS, Immutable.js or another push-model.
      </p>

      <h3>Incredible tooling</h3>
      <p>
        Build features quickly with simple, declarative templates. Extend the template language with your own components
        and use a wide array of existing components. Get immediate Angular-specific help and feedback with nearly every
        IDE and editor. All this comes together so you can focus on building amazing apps rather than trying to make the
        code work.
      </p>

      <h3>Loved by millions</h3>
      <p>
        From prototype through global deployment, Angular delivers the productivity and scalable infrastructure that
        supports Google's largest applications.
      </p>

      <h3>What is Angular?</h3>

      <p>
        Angular is a platform that makes it easy to build applications with the web. Angular combines declarative
        templates, dependency injection, end to end tooling, and integrated best practices to solve development
        challenges. Angular empowers developers to build applications that live on the web, mobile, or the desktop
      </p>

      <h3>Architecture overview</h3>

      <p>
        Angular is a platform and framework for building client applications in HTML and TypeScript. Angular is itself
        written in TypeScript. It implements core and optional functionality as a set of TypeScript libraries that you
        import into your apps.
      </p>

      <p>
        The basic building blocks of an Angular application are NgModules, which provide a compilation context for
        components. NgModules collect related code into functional sets; an Angular app is defined by a set of
        NgModules. An app always has at least a root module that enables bootstrapping, and typically has many more
        feature modules.
      </p>

      <p>
        Components define views, which are sets of screen elements that Angular can choose among and modify according to
        your program logic and data. Every app has at least a root component.
      </p>

      <p>
        Components use services, which provide specific functionality not directly related to views. Service providers
        can be injected into components as dependencies, making your code modular, reusable, and efficient.
      </p>

      <p>
        Both components and services are simply classes, with decorators that mark their type and provide metadata that
        tells Angular how to use them.
      </p>

      <p>
        The metadata for a component class associates it with a template that defines a view. A template combines
        ordinary HTML with Angular directives and binding markup that allow Angular to modify the HTML before rendering
        it for display.
      </p>

      <p>
        The metadata for a service class provides the information Angular needs to make it available to components
        through Dependency Injection (DI).
      </p>

      <p>
        An app's components typically define many views, arranged hierarchically. Angular provides the Router service to
        help you define navigation paths among views. The router provides sophisticated in-browser navigational
        capabilities.
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button matButton mat-dialog-close color="secondary">Cancel</button>
      <button matButton [mat-dialog-close]="true" cdkFocusInitial>Install</button>
    </mat-dialog-actions>
  `,
  imports: [MatDialogModule, MatButtonModule, JsonPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
class DialogContentExampleDialog {
  data = inject(MAT_DIALOG_DATA);
}

interface Task {
  name: string;
  completed: boolean;
  subtasks?: Task[];
}
